import { getCurrentMerchantTokenSnapshot } from '@contexts/admin/MerchantContext';
import i18n from '@i18n/i18n';
import { message } from 'antd';
import { getUserTimezone } from '@utils/dateTime';

import authService from './authService';

const BASE_URL = '/api/admin/v1/';

// 獲取請求 headers，包含認證資訊和 merchant_user_token
const getApiHeaders = (merchantUserToken = null) => {
  const headers = authService.getAuthHeaders();

  // 如果有 merchant_user_token，加入到 Authorization header
  if (merchantUserToken) {
    headers.Authorization = `Bearer ${merchantUserToken}`;
  }

  return headers;
};

// adminApiFetch - 完全相容 fetch API，但會自動加上認證和 merchant_user_token
export const adminApiFetch = async (url, options = {}) => {
  const { headers: customHeaders, merchantUserToken, params, ...fetchOptions } = options;

  // 獲取當前 merchant_user_token（優先使用傳入的，否則從 Context 獲取）
  const effectiveToken = merchantUserToken ?? getCurrentMerchantTokenSnapshot();

  // 如果 URL 不是完整的 URL，則加上 BASE_URL
  // 確保不會有雙斜線
  let normalizedUrl = url.startsWith('/') ? url.slice(1) : url;
  let fullUrl = url.startsWith('http') ? url : `${BASE_URL}${normalizedUrl}`;

  // 自動處理 GET/HEAD 請求的 params
  if (params) {
    const urlObj = new URL(fullUrl, window.location.origin);
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        if (Array.isArray(params[key])) {
          params[key].forEach(val => {
            const finalVal = (val && typeof val.toISOString === 'function') ? val.toISOString() : val;
            urlObj.searchParams.append(`${key}[]`, finalVal);
          });
        } else if (typeof params[key] === 'object' && typeof params[key].toISOString !== 'function') {
          // Handle nested objects
          Object.keys(params[key]).forEach(subKey => {
            const subVal = params[key][subKey];
            if (subVal !== undefined && subVal !== null) {
              const finalSubVal = (subVal && typeof subVal.toISOString === 'function') ? subVal.toISOString() : subVal;
              urlObj.searchParams.append(`${key}[${subKey}]`, finalSubVal);
            }
          });
        } else {
          const finalVal = (params[key] && typeof params[key].toISOString === 'function') ? params[key].toISOString() : params[key];
          urlObj.searchParams.append(key, finalVal);
        }
      }
    });
    fullUrl = urlObj.toString();
  }

  // 如果是 FormData，不要設定任何 headers（讓瀏覽器自動設定 Content-Type 與 boundary）
  const isFormData = fetchOptions.body instanceof FormData;

  // 獲取當前 locale 和 timezone
  const currentLocale = i18n.language || 'zh';
  const timezone = getUserTimezone();

  // 建立基礎 headers（包含 locale 和 timezone）
  const baseHeaders = {
    'Accept-Language': currentLocale,
    'X-Timezone': timezone,
    ...customHeaders
  };

  const headers = isFormData ? {
    ...baseHeaders
  } : {
    ...getApiHeaders(effectiveToken),
    ...baseHeaders
  };

  const config = {
    headers,
    credentials: 'include', // Important: send cookies with every request
    ...fetchOptions
  };

  // 如果是 FormData，需要添加 Authorization header（如果有 token）
  if (isFormData && effectiveToken) {
    headers.Authorization = `Bearer ${effectiveToken}`;
  }

  // 如果有 body 且不是 FormData，自動 JSON 序列化
  if (config.body && !isFormData) {
    if (typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }
    // 設定 Content-Type（無論是 object 還是已經 stringify 過的 string）
    if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
  }

  try {
    const response = await fetch(fullUrl, config);

    // 處理 401 未授權錯誤
    if (response.status === 401) {
      // 不要在這裡進行重定向或清理認證，讓 AuthContext 統一處理
      // AuthContext 的 checkAuthStatus 會處理 401 錯誤並更新認證狀態
      // React Router 會根據認證狀態自動導向到登入頁面
      throw new Error('Unauthorized');
    }

    // 處理 403 錯誤
    // 預設視為一般權限不足；若後端回傳 `ip_restricted` 則改顯示 IP 白名單訊息（含當下 IP），
    // 避免使用者誤以為是帳號權限問題
    if (response.status === 403) {
      let errorCode = null;
      let interpolations = {};
      try {
        const body = await response.clone().json();
        errorCode = body?.message;
        interpolations = body?.interpolations || {};
      } catch (parseErr) {
        // body 非 JSON：照原邏輯走通用 403
      }

      if (errorCode === 'ip_restricted') {
        message.error(
          i18n.t('common.error.ip_restricted', { ip: interpolations.ip || '' }),
          8
        );
        throw new Error('IpRestricted');
      }

      message.error(i18n.t('common.error.permission_denied'));
      throw new Error('Forbidden');
    }

    return response;
  } catch (err) {
    throw err;
  }
};

export default adminApiFetch;
